import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SortingState } from '@tanstack/react-table';
import { ProductOfferOrderWithRelations } from '@durma-soft/gros-sdk/dist/types/product-offers';

import { DataTable } from '@/components/shared/data-table';

import { allOrderColumns } from '@/config/restaurant/platform-product-offers';

interface RestaurantOrdersGroupProps {
  name: string;
  orders: ProductOfferOrderWithRelations[];
}

export const orderTotalsByCurrency = (
  orders: ProductOfferOrderWithRelations[],
) => {
  const totals = new Map<string, number>();
  for (const order of orders) {
    // Sum the per-row values exactly as displayed (rounded to 2 decimals),
    // so the totals line always matches the visible column.
    const rowTotal = Number(
      (Number(order.price_per_unit) * Number(order.quantity)).toFixed(2),
    );
    totals.set(order.currency, (totals.get(order.currency) ?? 0) + rowTotal);
  }
  return [...totals.entries()].map(
    ([currency, total]) => `${total.toFixed(2)} ${currency}`,
  );
};

export const RestaurantOrdersGroup = ({
  name,
  orders,
}: RestaurantOrdersGroupProps) => {
  const { t } = useTranslation();
  const [sorting, setSorting] = useState<SortingState>([]);

  const columns = useMemo(() => allOrderColumns(t), [t]);

  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-xl font-medium">{name}</h3>
      <DataTable
        columns={columns}
        data={orders}
        sorting={sorting}
        setSorting={setSorting}
      />
      <p className="self-end text-sm font-semibold">
        {t('product-offers.total')}: {orderTotalsByCurrency(orders).join(' + ')}
      </p>
    </div>
  );
};
